import {
  RCL_MULTIPLIER_PAD_VALUES,
  RCL_MULTIPLIER_PAYMENT_TERMS,
  RCL_MULTIPLIER_RISK_CLASS_CODES,
} from '@repo/constants'
import { z } from 'zod'

/**
 * RCL Multipliers import. The CSV round-trips the export 1:1, so the header list and its order match
 * the export shape exactly: payment term × risk class (code) × PAD → multiplier.
 */
export const importRclMultipliersHeaders = [
  'payment_term',
  'risk_class',
  'pad',
  'multiplier',
] as const

const paymentTerms: readonly string[] = RCL_MULTIPLIER_PAYMENT_TERMS
const riskClassCodes: readonly string[] = RCL_MULTIPLIER_RISK_CLASS_CODES
const padValues: readonly number[] = RCL_MULTIPLIER_PAD_VALUES

/**
 * Rows whose payment term / risk class / PAD are not recognized by the system fail parsing here and
 * are tracked as invalid (never inserted). `multiplier` is kept as a string (the column is
 * numeric(10,4)); a blank clears the value (stored as null), a negative or non-numeric value is
 * rejected.
 */
export const importRclMultipliersSchema = z.object({
  payment_term: z
    .string()
    .transform((value) => value.trim())
    .refine((value) => paymentTerms.includes(value)),
  risk_class: z
    .string()
    .transform((value) => value.trim())
    .refine((value) => riskClassCodes.includes(value)),
  pad: z.coerce
    .number()
    .int()
    .refine((value) => padValues.includes(value)),
  multiplier: z
    .string()
    .transform((value) => value.trim())
    .refine((value) => value === '' || (!isNaN(Number(value)) && Number(value) >= 0))
    .transform((value) => (value === '' ? null : value)),
})

export type ImportRclMultipliersRecord = z.infer<typeof importRclMultipliersSchema>
